import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import {
    Box,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    makeStyles,
    withStyles,
} from '@material-ui/core';
import { Modal, Button } from 'antd';
import { CSVLink } from 'react-csv';
import moment from 'moment';
import HttpService from '../../services/httpService';
import { buildMDaySummaryCsv } from '../../utils/designSummaryTableCsv';
import { setLoading, setSnackInfo } from '../../redux/actions/appAction';
import {
    M_DAY_BUCKETS,
    getMDayBucketDisplayLabels,
    buildMDayExperienceCenterBreakdown,
    extractMColumnLeads,
    parseDesignHealthMDayUrlParams,
    sortMDayLeadsByClosureDate,
    resolveLeadCurrentStage,
    resolveLeadDesignerName,
    resolveLeadOdmManagerName,
    resolveLeadCityAgmName,
    formatDaysLapsedFromCommittedDate,
    formatDaysLapsedInCurrentDesignStage,
} from '../../utils/designHealthMDayBuckets';

const NAVY = '#1C375D';
const ORANGE = '#F47E46';
const GRAND_TOTAL_GREEN = '#355025';
const STRIPE_ODD = '#F3F6FA';
const STRIPE_EVEN = '#FFFFFF';
const TABLE_BORDER = '#e8e0dc';
const TABLE_HEAD_BG = NAVY;
const TABLE_HEAD_TEXT = '#FFF2EB';

const StyledTableCell = withStyles(() => ({
    head: {
        backgroundColor: NAVY,
        color: '#FFF2EB',
        fontWeight: 500,
    },
    body: {
        fontSize: 14,
    },
}))(TableCell);

const StyledTableRow = withStyles({
    root: {},
})(TableRow);

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(1, 2),
    },
    pageHeader: {
        marginBottom: theme.spacing(1),
    },
    title: {
        color: NAVY,
        fontWeight: 700,
        marginBottom: theme.spacing(0.5),
    },
    subtitle: {
        color: theme.palette.text.secondary,
        fontSize: 14,
        marginBottom: theme.spacing(1),
    },
    bordercolors: {
        border: `1px solid ${TABLE_BORDER}`,
    },
    summaryTablePaper: {
        borderRadius: 8,
        border: `1px solid ${TABLE_BORDER}`,
        boxShadow: '0 1px 4px rgba(28, 55, 93, 0.08)',
    },
    tableScrollContainer: {
        maxHeight: 'min(600px, calc(100vh - 260px))',
        overflow: 'auto',
        position: 'relative',
        WebkitOverflowScrolling: 'touch',
        '& .MuiTable-root': {
            borderCollapse: 'separate',
            borderSpacing: 0,
        },
    },
    stickyTableHead: {
        position: 'sticky',
        top: 0,
        zIndex: 10,
        backgroundColor: TABLE_HEAD_BG,
        '& .MuiTableCell-head': {
            backgroundColor: `${TABLE_HEAD_BG} !important`,
            color: `${TABLE_HEAD_TEXT} !important`,
            boxShadow: `inset 0 -1px 0 ${TABLE_BORDER}`,
        },
    },
    summaryTable: {
        borderCollapse: 'separate',
        borderSpacing: 0,
    },
    tableScroll: {
        position: 'relative',
        zIndex: 1,
    },
    tableLoadingOverlay: {
        position: 'absolute',
        inset: 0,
        zIndex: 20,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.72)',
    },
    bodyCell: {
        textAlign: 'center',
        border: `1px solid ${TABLE_BORDER}`,
        fontSize: 14,
    },
    bodyCellOdd: {
        backgroundColor: STRIPE_ODD,
    },
    bodyCellEven: {
        backgroundColor: STRIPE_EVEN,
    },
    summaryHeadCell: {
        '&&': {
            backgroundColor: `${TABLE_HEAD_BG} !important`,
            color: `${TABLE_HEAD_TEXT} !important`,
        },
        textAlign: 'center',
        fontWeight: 500,
        border: `1px solid ${TABLE_BORDER}`,
        verticalAlign: 'middle',
        padding: '10px 8px',
        lineHeight: 1.35,
        whiteSpace: 'nowrap',
    },
    expCentreHeadCell: {
        position: 'sticky',
        left: 0,
        zIndex: 16,
        minWidth: 168,
        maxWidth: 168,
        width: 168,
        fontWeight: 600,
        verticalAlign: 'middle',
        boxShadow: '2px 0 4px rgba(0,0,0,0.08)',
    },
    expCentreBodyCell: {
        position: 'sticky',
        left: 0,
        zIndex: 4,
        minWidth: 168,
        maxWidth: 168,
        verticalAlign: 'middle',
        textAlign: 'center',
        border: `1px solid ${TABLE_BORDER}`,
        fontWeight: 600,
        backgroundColor: `${TABLE_HEAD_BG} !important`,
        color: `${TABLE_HEAD_TEXT} !important`,
        boxShadow: '2px 0 4px rgba(0,0,0,0.06)',
    },
    expCentreTotalsCell: {
        position: 'sticky',
        left: 0,
        zIndex: 5,
        minWidth: 168,
        maxWidth: 168,
        textAlign: 'center',
        border: `1px solid ${TABLE_BORDER}`,
        borderTop: `2px solid ${NAVY}`,
        fontWeight: 700,
        fontSize: 15,
        backgroundColor: `${TABLE_HEAD_BG} !important`,
        color: `${TABLE_HEAD_TEXT} !important`,
        boxShadow: '2px 0 4px rgba(0,0,0,0.06)',
    },
    clickableCount: {
        color: NAVY,
        textDecoration: 'underline',
        cursor: 'pointer',
        fontWeight: 'normal',
    },
    clickableGrandTotal: {
        color: GRAND_TOTAL_GREEN,
        textDecoration: 'underline',
        cursor: 'pointer',
        fontWeight: 'bolder',
    },
    disabledCount: {
        color: NAVY,
        fontWeight: 'normal',
    },
    totalsTableRow: {
        '& td': {
            borderTop: `2px solid ${NAVY}`,
        },
    },
    totalsRowCell: {
        textAlign: 'center',
        backgroundColor: '#FFFFFF',
        border: `1px solid ${TABLE_BORDER}`,
        fontWeight: 700,
        fontSize: 15,
        color: NAVY,
    },
    totalsRowCount: {
        color: NAVY,
        textDecoration: 'underline',
        cursor: 'pointer',
        fontWeight: 700,
    },
    totalsRowCountDisabled: {
        color: NAVY,
        fontWeight: 700,
    },
    emptyState: {
        textAlign: 'center',
        padding: theme.spacing(3),
        color: theme.palette.text.secondary,
    },
    csv: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: 12,
        flexShrink: 0,
    },
    tableDownloadRow: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    summaryDownloadBtn: {
        backgroundColor: GRAND_TOTAL_GREEN,
        color: '#FFF2EB',
    },
    detailModalContent: {
        display: 'flex',
        flexDirection: 'column',
        maxHeight: 'calc(80vh - 110px)',
    },
    detailTable: {
        flex: 1,
        minHeight: 240,
        maxHeight: 'calc(80vh - 160px)',
        overflow: 'auto',
        width: '100%',
    },
    detailTableInner: {
        minWidth: 1880,
    },
    detailBodyCell: {
        border: `1px solid ${TABLE_BORDER}`,
        fontSize: 13,
    },
    detailBodyCellOdd: {
        backgroundColor: STRIPE_ODD,
    },
    detailBodyCellEven: {
        backgroundColor: STRIPE_EVEN,
    },
}));

const DETAIL_CSV_HEADERS = [
    { label: 'Sl. No.', key: 'index' },
    { label: 'Customer Name', key: 'customerName' },
    { label: 'Project No.', key: 'projectNo' },
    { label: 'Lead No.', key: 'leadNo' },
    { label: 'Value (INR)', key: 'value' },
    { label: 'City', key: 'city' },
    { label: 'Exp. Ctr. Name', key: 'expCenter' },
    { label: 'Current Stage', key: 'currentStage' },
    { label: 'Days Lapsed in the Last Design Stage', key: 'daysLapsedInCurrentDesignStage' },
    { label: 'Designer', key: 'designer' },
    { label: 'ODM Manager', key: 'odmManager' },
    { label: 'Design AGM', key: 'cityAgm' },
    { label: 'Sales Won Date', key: 'salesWonDate' },
    { label: 'Tentative Design Closure Date (Committed Date)', key: 'closureDate' },
    { label: 'Days Lapsed From Committed Date', key: 'daysLapsedFromCommitted' },
    { label: 'Projection DSO Commited At', key: 'projectionDSOCommitedUpdatedAt' },
    { label: 'DSO Month Change (Slip On)', key: 'designHealthSecondApprovalsDate' },
    { label: 'Internal Design Sign-Off Date', key: 'internalDesignSignOffDate' },
];

const formatDetailRows = (items = []) =>
    items.map((item, i) => ({
        _id: item?._id,
        index: i + 1,
        customerName: item?.customer?.name || 'N/A',
        projectNo: item?.erpProjectNo || 'N/A',
        leadNo: item?.lead_no || 'N/A',
        value: item?.staticSalesWonBudget ?? 'N/A',
        city: item?.cityName || 'N/A',
        expCenter: item?.experienceCenterName || 'N/A',
        currentStage: resolveLeadCurrentStage(item),
        daysLapsedInCurrentDesignStage: formatDaysLapsedInCurrentDesignStage(item),
        designer: resolveLeadDesignerName(item),
        odmManager: resolveLeadOdmManagerName(item),
        cityAgm: resolveLeadCityAgmName(item),
        salesWonDate: item?.leadWonDate ? moment(item.leadWonDate).format('DD-MM-YYYY') : '-',
        closureDate: item?.closureDate ? moment(item.closureDate).format('DD-MM-YYYY') : '-',
        daysLapsedFromCommitted: formatDaysLapsedFromCommittedDate(item?.closureDate),
        projectionDSOCommitedUpdatedAt: item?.projectionDSOCommitedUpdatedAt
            ? moment(item.projectionDSOCommitedUpdatedAt).format('DD-MM-YYYY')
            : '-',
        designHealthSecondApprovalsDate: item?.designHealthSecondApprovalsDate
            ? moment(item.designHealthSecondApprovalsDate).format('DD-MM-YYYY')
            : '-',
        internalDesignSignOffDate: item?.internalDesignSignOffDate
            ? moment(item.internalDesignSignOffDate).format('DD-MM-YYYY')
            : '-',
    }));

const salesMonthTitle = (salesMonth) => {
    if (salesMonth === 'all') return 'All sales months';
    if (salesMonth === 'Total') return 'Grand total row (M)';
    return salesMonth;
};

const DesignHealthMDaySummary = (props) => {
    const classes = useStyles();
    const initialParams = useMemo(() => parseDesignHealthMDayUrlParams(), []);
    const [rows, setRows] = useState([]);
    const [displayBuckets, setDisplayBuckets] = useState(M_DAY_BUCKETS);
    const [lastBucketCaption, setLastBucketCaption] = useState('D-30 (through day 30)');
    const [summaryLoading, setSummaryLoading] = useState(true);
    const [detailOpen, setDetailOpen] = useState(false);
    const [detailTitle, setDetailTitle] = useState('');
    const [detailRows, setDetailRows] = useState([]);
    const [csvData, setCsvData] = useState([]);
    const detailTableRef = useRef(null);

    const loadSummary = useCallback(async () => {
        setSummaryLoading(true);
        try {
            const response = await HttpService.designHealthCommitmentData({
                startDate: initialParams.startDate,
                endDate: initialParams.endDate,
                selectedExperienceCenter: initialParams.selectedExperienceCenter,
                selectedLocation: initialParams.selectedLocation,
                userId: initialParams.userId,
                userExpeCenterId: initialParams.userExpeCenterId,
                monthType: initialParams.monthType,
            });
            const designData = Array.isArray(response?.data) ? response.data : [];
            const mLeads = sortMDayLeadsByClosureDate(
                extractMColumnLeads(designData, initialParams.salesMonth)
            );
            const buckets = getMDayBucketDisplayLabels(mLeads);
            setDisplayBuckets(buckets);
            setLastBucketCaption(
                buckets.some((b) => b.key === 'd31')
                    ? 'D-31 (day 31 only). Other columns: D-5 (1–5), D-10 (6–10), D-15 (11–15), D-20 (16–20), D-25 (21–25), D-30 (26–30)'
                    : 'D-5 (1–5), D-10 (6–10), D-15 (11–15), D-20 (16–20), D-25 (21–25), D-30 (26–30)'
            );
            setRows(buildMDayExperienceCenterBreakdown(mLeads));
        } catch (error) {
            props.setSnackInfo(
                error?.response?.data?.message || error?.response?.data || 'Failed to load M day summary',
                'error'
            );
            setRows([]);
        } finally {
            setSummaryLoading(false);
        }
    }, [initialParams, props.setSnackInfo]);

    useEffect(() => {
        loadSummary();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!detailOpen || !detailTableRef.current) return;
        detailTableRef.current.scrollTop = 0;
        detailTableRef.current.scrollLeft = 0;
    }, [detailOpen, detailRows, detailTitle]);

    const openDetails = (bucketData, label) => {
        if (!bucketData?.count) return;
        const sorted = sortMDayLeadsByClosureDate(bucketData.data || []);
        setDetailRows(sorted);
        setCsvData(formatDetailRows(sorted));
        setDetailTitle(`${label} (${sorted.length})`);
        setDetailOpen(true);
    };

    const renderCount = (count, onClick, { isGrandTotal = false, isTotalsRow = false } = {}) => {
        if (!count) {
            return (
                <span className={isTotalsRow ? classes.totalsRowCountDisabled : classes.disabledCount}>
                    0
                </span>
            );
        }
        let countClass = classes.clickableCount;
        if (isGrandTotal) countClass = classes.clickableGrandTotal;
        else if (isTotalsRow) countClass = classes.totalsRowCount;

        return (
            <span
                className={countClass}
                role="button"
                tabIndex={0}
                onClick={onClick}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') onClick();
                }}
            >
                {count}
            </span>
        );
    };

    const dataRows = rows.filter((r) => r.experienceCenter !== 'Total');
    const totalRow = rows.find((r) => r.experienceCenter === 'Total');

    const summaryTableCsv = useMemo(
        () => buildMDaySummaryCsv(displayBuckets, rows),
        [displayBuckets, rows]
    );

    const summaryCsvFilename = useMemo(
        () => `design-health-m-day-summary-${moment().format('YYYY-MM-DD')}.csv`,
        [rows, displayBuckets]
    );

    return (
        <div className={classes.root}>
            <Box className={classes.pageHeader}>
                <Typography className={classes.title} variant="h5">
                    Design Health — M month day-wise summary
                </Typography>
                <Typography className={classes.subtitle}>
                    Scope: <strong>{salesMonthTitle(initialParams.salesMonth)}</strong> · M column leads by
                    experience centre. Closure day bands (each lead in one column only): {lastBucketCaption}.
                    Click a count for lead details.
                </Typography>
            </Box>

            <Box className={classes.tableDownloadRow}>
                {summaryTableCsv.data.length > 0 && !summaryLoading ? (
                    <CSVLink
                        data={summaryTableCsv.data}
                        headers={summaryTableCsv.headers}
                        filename={summaryCsvFilename}
                        style={{ textDecoration: 'none' }}
                    >
                        <Button variant="contained" className={classes.summaryDownloadBtn}>
                            Download table CSV
                        </Button>
                    </CSVLink>
                ) : (
                    <Button
                        variant="contained"
                        className={classes.summaryDownloadBtn}
                        disabled={summaryLoading}
                        onClick={() =>
                            props.setSnackInfo('No summary data to download for the current filters.', 'warning')
                        }
                    >
                        Download table CSV
                    </Button>
                )}
            </Box>

            <div className={classes.tableScroll}>
                {summaryLoading && (
                    <Box className={classes.tableLoadingOverlay} aria-busy="true" aria-label="Loading summary">
                        <CircularProgress style={{ color: ORANGE }} />
                    </Box>
                )}
                <TableContainer
                    component={Paper}
                    className={`${classes.summaryTablePaper} ${classes.tableScrollContainer}`}
                    style={{ marginTop: 0 }}
                >
                    <Table className={classes.summaryTable} style={{ minWidth: 900 }}>
                        <TableHead className={classes.stickyTableHead}>
                            <StyledTableRow>
                                <StyledTableCell
                                    className={`${classes.summaryHeadCell} ${classes.expCentreHeadCell}`}
                                >
                                    Experience Centre
                                </StyledTableCell>
                                {displayBuckets.map((b) => (
                                    <StyledTableCell key={b.key} className={classes.summaryHeadCell}>
                                        {b.label}
                                    </StyledTableCell>
                                ))}
                                <StyledTableCell className={classes.summaryHeadCell} style={{ fontSize: 15 }}>
                                    Grand Total
                                </StyledTableCell>
                            </StyledTableRow>
                        </TableHead>
                        <TableBody>
                            {dataRows.map((row, rowIndex) => {
                                const isOddRow = rowIndex % 2 === 0;
                                const stripeBody = isOddRow ? classes.bodyCellOdd : classes.bodyCellEven;
                                return (
                                    <StyledTableRow key={row.experienceCenter}>
                                        <StyledTableCell className={classes.expCentreBodyCell}>
                                            {row.experienceCenter}
                                        </StyledTableCell>
                                        {displayBuckets.map((b) => (
                                            <StyledTableCell
                                                key={b.key}
                                                className={`${classes.bordercolors} ${classes.bodyCell} ${stripeBody}`}
                                            >
                                                {renderCount(row[b.key]?.count, () =>
                                                    openDetails(
                                                        row[b.key],
                                                        `${row.experienceCenter} · ${b.label}`
                                                    )
                                                )}
                                            </StyledTableCell>
                                        ))}
                                        <StyledTableCell
                                            className={`${classes.bordercolors} ${classes.bodyCell} ${stripeBody}`}
                                        >
                                            {renderCount(
                                                row.total?.count,
                                                () =>
                                                    openDetails(row.total, `${row.experienceCenter} · Grand Total`),
                                                { isGrandTotal: true }
                                            )}
                                        </StyledTableCell>
                                    </StyledTableRow>
                                );
                            })}
                            {totalRow && (
                                <StyledTableRow className={classes.totalsTableRow}>
                                    <StyledTableCell className={classes.expCentreTotalsCell}>
                                        Total
                                    </StyledTableCell>
                                    {displayBuckets.map((b) => (
                                        <StyledTableCell
                                            key={b.key}
                                            className={`${classes.bordercolors} ${classes.totalsRowCell}`}
                                        >
                                            {renderCount(
                                                totalRow[b.key]?.count,
                                                () => openDetails(totalRow[b.key], `All Centres · ${b.label}`),
                                                { isTotalsRow: true }
                                            )}
                                        </StyledTableCell>
                                    ))}
                                    <StyledTableCell className={`${classes.bordercolors} ${classes.totalsRowCell}`}>
                                        {renderCount(
                                            totalRow.total?.count,
                                            () => openDetails(totalRow.total, 'All Centres · Grand Total'),
                                            { isGrandTotal: true, isTotalsRow: true }
                                        )}
                                    </StyledTableCell>
                                </StyledTableRow>
                            )}
                            {!dataRows.length && !summaryLoading && (
                                <TableRow>
                                    <StyledTableCell colSpan={displayBuckets.length + 2} className={classes.emptyState}>
                                        No M-month committed leads for the selected filters.
                                    </StyledTableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>

            <Modal
                title={detailTitle}
                centered
                visible={detailOpen}
                onCancel={() => setDetailOpen(false)}
                width="92%"
                style={{ maxWidth: 1280, top: 24 }}
                maskClosable
                footer={null}
                destroyOnClose
                bodyStyle={{
                    padding: '12px 24px 20px',
                    maxHeight: '80vh',
                    overflow: 'hidden',
                }}
            >
                <div className={classes.detailModalContent}>
                    <div className={classes.csv}>
                        <CSVLink
                            filename="design-health-m-day-details.csv"
                            data={csvData}
                            headers={DETAIL_CSV_HEADERS}
                            style={{ textDecoration: 'none' }}
                        >
                            <Button
                                style={{ backgroundColor: '#355025', color: '#FFF2EB' }}
                                variant="contained"
                            >
                                Download CSV
                            </Button>
                        </CSVLink>
                    </div>
                    <TableContainer
                        ref={detailTableRef}
                        component={Paper}
                        className={classes.detailTable}
                        elevation={1}
                    >
                        <Table size="small" stickyHeader className={classes.detailTableInner}>
                            <TableHead>
                                <TableRow>
                                    {DETAIL_CSV_HEADERS.map((h) => (
                                        <StyledTableCell key={h.key}>{h.label}</StyledTableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {detailRows.map((item, index) => {
                                    const detailStripe =
                                        index % 2 === 0
                                            ? classes.detailBodyCellOdd
                                            : classes.detailBodyCellEven;
                                    const detailCell = `${classes.detailBodyCell} ${detailStripe}`;
                                    const formatted = formatDetailRows([item])[0];
                                    return (
                                        <StyledTableRow key={item._id || index}>
                                            <StyledTableCell className={detailCell}>{index + 1}</StyledTableCell>
                                            <StyledTableCell className={detailCell}>
                                                {formatted.customerName}
                                            </StyledTableCell>
                                            <StyledTableCell className={detailCell}>
                                                {formatted.projectNo !== 'N/A' && item._id ? (
                                                    <Link
                                                        to={`/project-logs/${item._id}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{ color: '#1d4ed8', textDecoration: 'underline' }}
                                                    >
                                                        {formatted.projectNo}
                                                    </Link>
                                                ) : (
                                                    'NA'
                                                )}
                                            </StyledTableCell>
                                            <StyledTableCell className={detailCell}>
                                                {formatted.leadNo !== 'N/A' && item._id ? (
                                                    <Link
                                                        to={`/project-logs/${item._id}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{ color: '#1d4ed8', textDecoration: 'underline' }}
                                                    >
                                                        {formatted.leadNo}
                                                    </Link>
                                                ) : (
                                                    'NA'
                                                )}
                                            </StyledTableCell>
                                            <StyledTableCell className={detailCell}>{formatted.value}</StyledTableCell>
                                            <StyledTableCell className={detailCell}>{formatted.city}</StyledTableCell>
                                            <StyledTableCell className={detailCell}>{formatted.expCenter}</StyledTableCell>
                                            <StyledTableCell className={detailCell}>{formatted.currentStage}</StyledTableCell>
                                            <StyledTableCell className={detailCell}>
                                                {formatted.daysLapsedInCurrentDesignStage}
                                            </StyledTableCell>
                                            <StyledTableCell className={detailCell}>{formatted.designer}</StyledTableCell>
                                            <StyledTableCell className={detailCell}>{formatted.odmManager}</StyledTableCell>
                                            <StyledTableCell className={detailCell}>{formatted.cityAgm}</StyledTableCell>
                                            <StyledTableCell className={detailCell}>{formatted.salesWonDate}</StyledTableCell>
                                            <StyledTableCell className={detailCell}>{formatted.closureDate}</StyledTableCell>
                                            <StyledTableCell className={detailCell}>
                                                {formatted.daysLapsedFromCommitted}
                                            </StyledTableCell>
                                            <StyledTableCell className={detailCell}>
                                                {formatted.projectionDSOCommitedUpdatedAt}
                                            </StyledTableCell>
                                            <StyledTableCell className={detailCell}>
                                                {formatted.designHealthSecondApprovalsDate}
                                            </StyledTableCell>
                                            <StyledTableCell className={detailCell}>
                                                {formatted.internalDesignSignOffDate}
                                            </StyledTableCell>
                                        </StyledTableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </div>
            </Modal>
        </div>
    );
};

export default connect(null, { setLoading, setSnackInfo })(DesignHealthMDaySummary);
